import React, { useEffect, useState } from "react"
import { UserIcon } from "@heroicons/react/24/solid"
import { twMerge } from "tailwind-merge"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

/**
 * A person's picture where the site shows one: their own photo (the one
 * their Google account gives, or one they uploaded), else their initials,
 * else an empty avatar. The cartoon avatars of the old site (`avatar_<n>`)
 * count as no photo.
 */

// the photo a user record points at, or null
export function avatarPhoto(user) {
  const value = String(user?.avatar || "").trim()
  if (!value || /^avatar_\d+$/.test(value)) return null
  if (/^(https?:|data:|blob:)/.test(value) || value.startsWith("//")) return value
  // an uploaded one (user-avatars/…) is a media path
  return cmsUrl(value)
}

// the name the backend gives an account that has none of its own
const PLACEHOLDER_NAMES = ["aldoo user"]

// "Цветомир Христов" → "ЦХ", "ivan" → "I"; the email's first letter when there is no name
export function initialsOf(user) {
  const name = String(user?.name || "").trim()
  if (name && !PLACEHOLDER_NAMES.includes(name.toLowerCase())) {
    const words = name.split(/\s+/).filter((word) => /\p{L}/u.test(word))
    const letters = words.slice(0, 2).map((word) => word.match(/\p{L}/u)[0])
    if (letters.length) return letters.join("").toLocaleUpperCase()
  }
  const email = String(user?.email || "").trim()
  return email ? email[0].toLocaleUpperCase() : ""
}

export default function UserAvatar({ user, size = 40, className = "", "data-testid": testId }) {
  const photo = avatarPhoto(user)
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [photo])
  const initials = initialsOf(user)
  const box = { width: size, height: size }
  const round = twMerge("shrink-0 rounded-full overflow-hidden", className)

  if (photo && !failed) {
    return <img src={photo} alt="" style={box} className={twMerge(round, "object-cover")} onError={() => setFailed(true)} referrerPolicy="no-referrer" data-testid={testId} />
  }
  if (initials) {
    return (
      <span style={{ ...box, fontSize: Math.round(size * 0.4) }} className={twMerge(round, "inline-flex items-center justify-center bg-neutral border border-theme_white text-text font-semibold select-none")} data-testid={testId} aria-hidden>
        {initials}
      </span>
    )
  }
  return (
    <span style={box} className={twMerge(round, "inline-flex items-end justify-center bg-neutral text-text_secondary")} data-testid={testId} aria-hidden>
      <UserIcon style={{ width: size * 0.78, height: size * 0.78 }} />
    </span>
  )
}
